// Epicor parts sync API client.
//
// Reuses the shared `syncApi` axios instance from syncApi.js so the same
// X-Sync-API-Key / X-User interceptor and SYNC_AUTH_EVENT handling apply.
// SSE (EventSource) cannot send custom headers, so the stream endpoint is
// opened unauthenticated, exactly like the BigCommerce job stream.
import syncApi, { subscribeToJobStream } from './syncApi';

const baseURL = process.env.REACT_APP_API_URL || 'http://localhost:8000/api';

export const createEpicorJob = (payload) =>
  syncApi.post('/epicor/jobs', payload).then((r) => r.data);

export const listEpicorJobs = (params) =>
  syncApi.get('/epicor/jobs', { params }).then((r) => r.data);

export const getEpicorJob = (id) =>
  syncApi.get(`/epicor/jobs/${id}`).then((r) => r.data);

export const cancelEpicorJob = (id) =>
  syncApi.post(`/epicor/jobs/${id}/cancel`).then((r) => r.data);

export const getEpicorSummary = () =>
  syncApi.get('/epicor/summary').then((r) => r.data);

// ---- Customers (staging browse + BC link, read-only towards BigCommerce) ----

export const listEpicorCustomers = (params) =>
  syncApi.get('/epicor/customers', { params }).then((r) => r.data);

export const getEpicorCustomer = (company, custNum) =>
  syncApi.get(`/epicor/customers/${company}/${custNum}`).then((r) => r.data);

export const refreshBCCustomers = (companies) =>
  syncApi.post('/epicor/customers/refresh-bc', { companies }).then((r) => r.data);

export const setManualCustomerMatch = (company, custNum, bcCustomerId) =>
  syncApi
    .post(`/epicor/customers/${company}/${custNum}/match`, { bc_customer_id: bcCustomerId })
    .then((r) => r.data);

// ---- Sales orders (archival staging) ----

export const listEpicorOrders = (params) =>
  syncApi.get('/epicor/orders', { params }).then((r) => r.data);

export const getEpicorOrder = (company, orderNum) =>
  syncApi.get(`/epicor/orders/${company}/${orderNum}`).then((r) => r.data);

// ---- Invoices and credit notes ----

export const listEpicorInvoices = (params) =>
  syncApi.get('/epicor/invoices', { params }).then((r) => r.data);

export const getEpicorInvoice = (company, invoiceNum) =>
  syncApi.get(`/epicor/invoices/${company}/${invoiceNum}`).then((r) => r.data);

export const listCustomerOrders = (company, custNum, params) =>
  syncApi
    .get(`/epicor/customers/${company}/${custNum}/orders`, { params })
    .then((r) => r.data);

export const listCustomerInvoices = (company, custNum, params) =>
  syncApi
    .get(`/epicor/customers/${company}/${custNum}/invoices`, { params })
    .then((r) => r.data);

// ---- Credit availability (buy-on-account gating) ----

export const getCustomerCredit = (company, custNum) =>
  syncApi
    .get(`/epicor/customers/${company}/${custNum}/credit`)
    .then((r) => r.data);

export const listCredit = (params) =>
  syncApi.get('/epicor/credit', { params }).then((r) => r.data);

// Reads a page of customers straight from Epicor, capped at 50 and rate-limited
// to one call per 10s per key -- the middleware holds a single shared Epicor
// connection that buyer checkout reads also queue on. Takes company-qualified
// refs so a page mixing AU and NZ is one call. Returns { credit, missing }, both
// keyed "{company}-{cust_num}"; `missing` lists customers that could not be read.
export const refreshCreditLive = (customers) =>
  syncApi.post('/epicor/credit/live', { customers }).then((r) => r.data);

// live=true re-pulls the customer from Epicor before recomputing; live=false is
// a cheap recompute from staging. Returns a job id -- the refresh is async.
export const refreshCustomerCredit = (company, { custNum, custId, live = true }) =>
  syncApi
    .post('/epicor/credit/refresh', {
      company,
      cust_num: custNum ?? null,
      cust_id: custId ?? null,
      live,
    })
    .then((r) => r.data);

export const subscribeToEpicorJob = (id, onProgress, onClose) =>
  subscribeToJobStream(`${baseURL}/epicor/jobs/${id}/stream`, onProgress, onClose);
